import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import { REMOVE_QUESTION_PASSAGE } from 'queries/questionPassage'
import { removeQuestionPassage, removeQuestionPassageVariables } from '__generatedTypes__/removeQuestionPassage'

export type RemoveButtonProps = {
  questionId: string
  submitting: boolean
  setSubmitting: (submitting: boolean) => void
  setRemoveError?: Dispatch<SetStateAction<boolean>>
  isTemplate?: boolean
}

export const RemoveButton = ({
  questionId,
  submitting,
  setSubmitting,
  setRemoveError,
  isTemplate = false,
}: RemoveButtonProps) => {
  const { navigate } = useLink()
  const [removeQuestion] = useMutation<removeQuestionPassage, removeQuestionPassageVariables>(REMOVE_QUESTION_PASSAGE)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleRemove = useCallback(async () => {
    if (submitting) {
      return
    }

    setSubmitting(true)
    try {
      await removeQuestion({
        variables: {
          id: questionId,
        },
      })
      setRemoveError?.(false)
      toast.success(isTemplate ? 'CARS Question template deleted successfully' : 'CARS Question deleted successfully')

      navigate(cmsRoutes.CARS_QUESTION_LIST)
    } catch (e) {
      console.error(e)
      setRemoveError?.(true)
      toast.error(isTemplate ? 'Failed to delete CARS Question template' : 'Failed to delete CARS Question')
    }
    setIsModalOpen(false)
    setSubmitting(false)
  }, [questionId, submitting, navigate])

  return (
    <>
      <Button type="button" variant="outlined" color="red" disabled={submitting} onClick={() => setIsModalOpen(true)}>
        Delete
      </Button>
      <ConfirmationModal
        open={isModalOpen}
        title={isTemplate ? 'Deleting CARS Question Template' : 'Deleting CARS Question'}
        subtitle={
          <>
            Are you sure you want to delete this <br />
            {isTemplate ? 'CARS question template' : 'CARS question'}? This action cannot be changed.
          </>
        }
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleRemove}
      />
    </>
  )
}
